import { Seg } from '../../ui/Block';
import type { Command, Lang, SimConfig, WorldGpu } from './engine';

export type StockMode = 'normal' | 'noneUnderCap' | 'none';
export type VastMode = 'fast' | 'slow' | 'refusing';
export interface Situation {
  live: number; lang: Lang; voices: number; linesPerVoice: number; reading: number;
  stock: StockMode; vast: VastMode; vastGpu: string; vastLinesPerMin: number; podLinesPerMin: number; vastPerH: number; liveMin: number;
}

export const SITUATION: Situation = {
  live: 4, lang: 'multilingual', voices: 3, linesPerVoice: 6, reading: 0,
  stock: 'normal', vast: 'fast', vastGpu: 'RTX 4090', vastLinesPerMin: 140, podLinesPerMin: 60, vastPerH: 0.35, liveMin: 90,
};

export const PRESETS: { label: string; hint: string; s: Partial<Situation> }[] = [
  { label: 'Today: 4 live × 3 voices', hint: 'a normal afternoon: RunPod has stock, Vast boots in ~45 s', s: {} },
  { label: '30 live voices on one 4090', hint: '30 streams with one voice each: ~180 lines a minute against ~140 a 4090 makes', s: { live: 30, voices: 1 } },
  { label: 'RunPod sold out at or under the cap', hint: 'only dearer cards are free: transcription stays on Fly, voices on Vast', s: { live: 8, voices: 2, stock: 'noneUnderCap' } },
  { label: 'Vast slow (1–3 min)', hint: 'after 90 s RunPod races a speech-only pod', s: { live: 4, voices: 2, vast: 'slow' } },
  { label: 'Vast refusing', hint: 'every Vast ask refused: a RunPod speech-only pod speaks', s: { live: 4, voices: 2, vast: 'refusing' } },
  { label: 'A reading of 3,000 lines', hint: 'a file read aloud, no live session', s: { live: 0, voices: 0, reading: 3000 } },
  { label: 'Nothing anywhere', hint: 'RunPod has nothing and Vast refuses', s: { live: 6, voices: 2, stock: 'none', vast: 'refusing' } },
];

const VAST: Record<VastMode, { freeP: number; bootS: number; jitterS: number }> = {
  fast: { freeP: 1, bootS: 45, jitterS: 8 },
  slow: { freeP: 1, bootS: 120, jitterS: 35 },
  refusing: { freeP: 0, bootS: 45, jitterS: 8 },
};

export function situationConfig(c: SimConfig, s: Situation): SimConfig {
  const b = c.balancer;
  const cap = b.maxFirstPerH ?? 1.09;
  const names = [...new Set([...(b.gpus ?? []).map((g) => g.name), ...b.gpuTypes.map((g) => g.name)])];
  const perH = (n: string) => b.gpus?.find((g) => g.name === n)?.perH ?? b.gpuTypes.find((g) => g.name === n)?.perH ?? 0;
  const free = (n: string) => (s.stock === 'none' ? 0 : s.stock === 'noneUnderCap' && perH(n) <= cap + 1e-9 ? 0 : 1);
  const gpus: WorldGpu[] = names.map((n) => ({ ...(c.world.gpus?.find((g) => g.name === n) ?? { brokenHostP: null, bootScale: 1 }), name: n, freeP: free(n) }));
  const v = VAST[s.vast];
  const sm = b.speechMachines ?? { enabled: true, maxPerH: 0.6, readyTimeoutS: 900, heartbeatTimeoutS: 45, retryS: 120, racers: 3, bootCapS: 150 };
  return {
    ...c,
    balancer: {
      ...b, enabled: true, strategy: 'gpuFirst', settleS: 0, moveCooldownS: 0, idleStopS: 0, keepStopped: 0, minPods: 0, maxMovesPerPod: Math.max(4, b.maxMovesPerPod),
      flyPool: Math.max(b.flyPool, s.live + 10),
      speechMachines: { ...sm, enabled: true, runpod: true },
    },
    traffic: { ...c.traffic, generate: true, arrivalsPerH: 0, liveMedianMin: s.liveMin, liveSigma: 0.2, voicesPerLive: s.voices, voiceLinesPerMin: s.linesPerVoice },
    world: {
      ...c.world, gpus, brokenHostP: Math.min(c.world.brokenHostP, 0.05),
      vastFreeP: v.freeP, vastBootS: v.bootS, vastBootJitterS: v.jitterS, vastGpu: s.vastGpu, vastPerH: s.vastPerH,
      speechLinesPerMin: s.vastLinesPerMin, speechGpus: [{ gpu: s.vastGpu, linesPerMin: s.vastLinesPerMin }], podSpeechLinesPerMin: s.podLinesPerMin,
    },
  };
}

export function situationCommands(s: Situation): Command[] {
  const out: Command[] = [];
  if (s.live > 0) out.push({ cmd: 'rush', n: s.live, lang: s.lang, kind: 'live' });
  if (s.reading > 0) out.push({ cmd: 'reading', lines: Math.round(s.reading) });
  return out;
}

function Num({ label, v, on, step = 1, hint, min = 0 }: { label: string; v: number; on: (v: number) => void; step?: number; hint?: string; min?: number }) {
  return (
    <label className="field" style={{ margin: 0 }}>
      <span className="field__label">{label}</span>
      <input className="field__input mono" type="number" step={step} min={min} value={Number(v.toFixed(2))} onChange={(e) => { const x = Number(e.target.value); if (Number.isFinite(x)) on(Math.max(min, x)); }} />
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Scenarios({ s, set, onStart, onAdd }: { s: Situation; set: (s: Situation) => void; onStart: () => void; onAdd: () => void }) {
  const wanted = s.live * s.voices * s.linesPerVoice;
  const machines = s.vastLinesPerMin > 0 ? Math.ceil(wanted / (0.8 * s.vastLinesPerMin)) : 0;
  const up = (x: Partial<Situation>) => set({ ...s, ...x });
  return (
    <div style={{ display: 'grid', gap: 14, padding: 14 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="tbl__h" style={{ marginRight: 4 }}>PRESETS</span>
        {PRESETS.map((p) => (
          <button key={p.label} type="button" className="btn btn--sm" title={p.hint} onClick={() => set({ ...SITUATION, ...p.s })}>{p.label}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
        <Num label="Live streams" v={s.live} on={(v) => up({ live: Math.round(v) })} />
        <Num label="Voice languages each" v={s.voices} on={(v) => up({ voices: v })} step={0.5} hint="spoken translations per stream" />
        <Num label="Lines a minute a voice" v={s.linesPerVoice} on={(v) => up({ linesPerVoice: v })} step={0.1} hint="measured 4.6–7.5" />
        <Num label="Stream length (min)" v={s.liveMin} on={(v) => up({ liveMin: Math.max(1, v) })} />
        <Num label="Reading backlog (lines)" v={s.reading} on={(v) => up({ reading: Math.round(v) })} step={100} hint="a file read aloud" />
        <Num label={`${s.vastGpu || 'Speech GPU'}: lines a minute`} v={s.vastLinesPerMin} on={(v) => up({ vastLinesPerMin: v })} step={5} hint="live + batch; a 4090 ~140 today" />
        <Num label="Borrowed pod: lines a minute" v={s.podLinesPerMin} on={(v) => up({ podLinesPerMin: v })} step={5} hint="a transcription pod's spare room, until a speech machine is up" />
        <Num label="Vast $/h, all in" v={s.vastPerH} on={(v) => up({ vastPerH: v })} step={0.01} hint="0.60 at most" />
        <label className="field" style={{ margin: 0 }}>
          <span className="field__label">Speech GPU on Vast</span>
          <input className="field__input mono" value={s.vastGpu} onChange={(e) => up({ vastGpu: e.target.value })} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: '10px 18px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="tbl__h">LANGUAGE</span>
        <Seg<Lang> value={s.lang} onChange={(v) => up({ lang: v })} options={[{ value: 'multilingual', label: 'Multilingual' }, { value: 'english', label: 'English' }]} />
        <span className="tbl__h">RUNPOD STOCK</span>
        <Seg<StockMode> value={s.stock} onChange={(v) => up({ stock: v })} options={[{ value: 'normal', label: 'Normal' }, { value: 'noneUnderCap', label: 'Only over the cap' }, { value: 'none', label: 'Nothing' }]} />
        <span className="tbl__h">VAST</span>
        <Seg<VastMode> value={s.vast} onChange={(v) => up({ vast: v })} options={[{ value: 'fast', label: 'Fast ~45 s' }, { value: 'slow', label: 'Slow 1–3 min' }, { value: 'refusing', label: 'Refusing' }]} />
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn--primary" onClick={onStart} title="A fresh day: every live session gets the GPU, voices go to Vast first, no random traffic">Start this situation</button>
        <button type="button" className="btn" onClick={onAdd} title="The running day takes these settings now and these streams and reading are added">Add to the running day</button>
        <span style={{ fontSize: 12, color: 'var(--ink-62)' }}>
          {wanted > 0 ? `${Math.round(wanted)} live voice lines a minute; at ${Math.round(s.vastLinesPerMin)} a machine, ${machines} speech machine${machines === 1 ? '' : 's'} keep under 80%.` : 'No live voices.'}
          {s.reading > 0 ? ` A reading of ${s.reading} lines: ~${Math.ceil(s.reading / Math.max(1, s.vastLinesPerMin))} min on one machine.` : ''}
        </span>
      </div>
    </div>
  );
}
